import { useEffect, useState } from 'react';
import { createRoute, stripSearchParams } from '@tanstack/react-router';

import { rootRoute, useAppRouterInitial } from '../routerBase';
import { useDiscount } from '../../hooks/useDiscount';
import { useSurcharge } from '../../hooks/useSurcharge';
import {
  PRICE_MODIFIERS_SEARCH_DEFAULTS,
  parsePriceModifiersSearch,
} from '../../hooks/usePriceModifiersRouterSearch';
import { TableViewSkeleton } from '../../components/Skeleton/TableViewSkeleton';
import { EditSkeleton } from '../../components/Skeleton/EditSkeleton';
import { useTranslation } from '../../hooks/useTranslation';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { CAPABILITIES } from '../../lib/capabilities';
import { requireCapability } from '../requireCapability';
// Type-only imports so the per-route bundles stay split — the runtime modules
// are loaded via dynamic import() inside each route's effect (see below).
import type PriceModifiers from '../../components/PriceModifiers/index';
import type EditPriceModifier from '../../components/EditPriceModifier/index';

export const priceModifiersIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/price_modifiers',
  validateSearch: parsePriceModifiersSearch,
  search: { middlewares: [stripSearchParams(PRICE_MODIFIERS_SEARCH_DEFAULTS)] },
  component: function PriceModifiersIndexRoute() {
    const { t } = useTranslation();
    const { currentUser } = useAppRouterInitial();
    useDocumentTitle(t('common.page_titles.price_modifiers'));
    const [Component, setComponent] = useState<typeof PriceModifiers | null>(null);
    useEffect(() => {
      import('../../components/PriceModifiers/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component) {
      return <TableViewSkeleton label={t('common.loading')} />;
    }
    return <Component currentUser={currentUser} />;
  },
});

export const discountEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/discounts/$id/edit',
  // RBAC: edit is a write-only surface. The guard redirects read-only users
  // (no price_modifiers.write) to the Price modifiers index before this route's
  // component runs. Client-side (replace) — no full page reload. The server
  // policy (DiscountsController#edit) enforces this too; this is UX.
  beforeLoad: requireCapability(CAPABILITIES.PRICE_MODIFIERS_WRITE, { to: '/price_modifiers' }),
  component: function DiscountEditRoute() {
    const params = discountEditRoute.useParams();
    const { initialPriceModifier } = useAppRouterInitial();
    const { t } = useTranslation();
    const idNum = Number(params.id);
    const seed =
      initialPriceModifier?.kind === 'discount' && initialPriceModifier.record.id === idNum
        ? initialPriceModifier.record
        : undefined;
    const { data } = useDiscount(idNum, {
      initialData: seed ? { discount: seed } : undefined,
    });
    useDocumentTitle(
      data?.discount ? t('common.page_titles.edit', { name: data?.discount.name }) : t('common.page_titles.discounts'),
    );
    const [Component, setComponent] = useState<typeof EditPriceModifier | null>(null);
    useEffect(() => {
      import('../../components/EditPriceModifier/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    const discount = data?.discount;
    if (!Component || !discount) {
      return <EditSkeleton label={t('common.loading')} />;
    }
    return <Component kind="discount" id={idNum} initialRecord={discount} />;
  },
});

export const surchargeEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/surcharges/$id/edit',
  // RBAC: write-only surface — the guard redirects read-only users to the Price
  // modifiers index before the component runs (server policy on
  // SurchargesController#edit enforces too).
  beforeLoad: requireCapability(CAPABILITIES.PRICE_MODIFIERS_WRITE, { to: '/price_modifiers' }),
  component: function SurchargeEditRoute() {
    const params = surchargeEditRoute.useParams();
    const { initialPriceModifier } = useAppRouterInitial();
    const { t } = useTranslation();
    const idNum = Number(params.id);
    const seed =
      initialPriceModifier?.kind === 'surcharge' && initialPriceModifier.record.id === idNum
        ? initialPriceModifier.record
        : undefined;
    const { data } = useSurcharge(idNum, {
      initialData: seed ? { surcharge: seed } : undefined,
    });
    useDocumentTitle(
      data?.surcharge ? t('common.page_titles.edit', { name: data?.surcharge.name }) : t('common.page_titles.surcharges'),
    );
    const [Component, setComponent] = useState<typeof EditPriceModifier | null>(null);
    useEffect(() => {
      import('../../components/EditPriceModifier/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    const surcharge = data?.surcharge;
    if (!Component || !surcharge) {
      return <EditSkeleton label={t('common.loading')} />;
    }
    return <Component kind="surcharge" id={idNum} initialRecord={surcharge} />;
  },
});
